import { InjectionToken, Signal, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { EditorClose } from '@sensika/sui/dialog';

/**
 * Everything `app-data-grid` takes from the hosting application, behind one
 * token so the grid stack has no import into `src/app` (WAP-1744). The web app
 * provides it in AppModule (`DataGridAdapterService`); another consumer fills
 * in its own. The optional members switch a capability off when absent.
 */
export interface DataGridAdapter {
  /** Text direction of the signed-in user's language. */
  readonly language: Signal<{ rtl: boolean }>;
  readonly user: Signal<DataGridUser>;
  readonly logger: DataGridLogger;
  /** Yes / no question to the user; emits once. */
  confirm(message: string): Observable<boolean>;
  /** Per-grid view-preset persistence. Absent: the grid runs without presets. */
  viewPresets?(viewId: Signal<string | null>): GridViewPresetStore;
  /** The application header's slot for the data view toggle and date range. Absent: not announced. */
  readonly headerTools?: DataGridHeaderTools;
  /** Opens the application's editor for a user row; the grid refreshes when it saved. Absent: user cells are inert. */
  readonly objectEditor?: DataGridObjectEditor;
}

export interface DataGridUser {
  /** The user's "list company objects" switch; a flip refetches grids that opt in. */
  readonly listCompanyObjects: boolean;
  readonly canManageCompanyPresets: boolean;
  readonly canManageSystemPresets: boolean;
}

export interface DataGridObjectEditor {
  editUser(id: string): EditorClose;
}

export interface DataGridLogger {
  debug(message: unknown, ...optionalParams: unknown[]): void;
}

export interface DataGridHeaderToolsState {
  dateRange?: {
    context: number;
    lockPeriod?: boolean;
    timePresetBar?: boolean;
  };
  dataView?: { open$: Observable<boolean>; toggle: () => void };
}

export interface DataGridHeaderTools {
  set(tools: DataGridHeaderToolsState): void;
  clear(): void;
}

/** Owner scopes of a view preset, as the backend nomenclature ids. */
export enum GridViewPresetScope {
  SYSTEM = 10,
  COMPANY = 30,
  USER = 40,
}

/** The preset fields the grid reads and writes; the application's row type may carry more. */
export interface GridViewPreset {
  id?: number | null;
  uuid?: string | null;
  view_id: string;
  /** Left out by the grid: the store binds a preset without a context to the signed-in user. */
  context_object_id?: string | null;
  context_object_class_id?: number | null;
  name: string;
  settings?: any;
  is_pinned?: boolean;
  is_default?: boolean;
  is_locked?: boolean;
  is_effective_default?: boolean;
  view_preset_scope_id?: number;
}

export type GridViewPresetFlags = Partial<
  Pick<GridViewPreset, 'is_default' | 'is_pinned'>
>;

/** What the save dialog returns; `null` when it was dismissed. */
export interface GridViewPresetDetails {
  name: string;
  is_pinned: boolean;
  is_default?: boolean;
  uuid?: string;
}

export interface GridViewPresetStore {
  readonly presets: Signal<GridViewPreset[] | undefined>;
  readonly status: Signal<'pending' | 'error' | 'success'>;
  /** Create (no uuid) or update; the store owns cache upkeep and toasts. */
  save(
    preset: GridViewPreset,
    onSuccess: (saved: GridViewPreset) => void,
  ): void;
  setFlags(preset: GridViewPreset, flags: GridViewPresetFlags): void;
  delete(preset: GridViewPreset, onSuccess: () => void): void;
  /** A new shared row of the preset's scope (admin). */
  createShared(preset: GridViewPreset): void;
  /** Copy a personal preset into a shared scope (admin). */
  promote(preset: GridViewPreset, scopeId: GridViewPresetScope): void;
  askDetails(preset?: GridViewPreset): Observable<GridViewPresetDetails | null>;
}

export const DATA_GRID_ADAPTER = new InjectionToken<DataGridAdapter>(
  'DataGridAdapter',
);

/** The grid with presets switched off: an empty, settled list and inert actions. */
export function noViewPresetStore(): GridViewPresetStore {
  return {
    presets: signal<GridViewPreset[]>([]),
    status: signal<'success'>('success'),
    save: () => {},
    setFlags: () => {},
    delete: () => {},
    createShared: () => {},
    promote: () => {},
    askDetails: () => of(null),
  };
}
